import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Heading,
  SimpleGrid,
  Text,
  VStack,
  Button,
  Flex,
  HStack,
  Tooltip,
  useColorModeValue,
  Spinner,
  Center,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useEnvData, EnvDataProvider } from "../../context/EnvDataContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faClockRotateLeft,
  faSignal,
  faCircleQuestion,
} from "@fortawesome/free-solid-svg-icons";
import { authFetch } from "../../utils/authFetch";
import { ChevronLeftIcon } from "@chakra-ui/icons"; 


// --- 1. สร้าง Object สำหรับเก็บค่าเกณฑ์ (Thresholds) ---
const sensorThresholds = {
  pm25:  { warning: 37.5, danger: 80 },
  pm10:  { warning: 80,   danger: 120 },
  tsp:   { warning: 200,  danger: 330 },
  noise: { warning: 70,   danger: 115 },
};

// --- 2. ปรับปรุง SensorDisplay ให้รองรับการเปลี่ยนสี ---
const SensorDisplay = ({ sensorKey, label, value, unit }) => {
  const displayValue = (val) =>
    val !== null && val !== undefined ? val.toFixed(1) : "-";

  // Logic ในการกำหนดสีของตัวเลข
  const getValueColor = (key, val) => {
    const limits = sensorThresholds[key];
    if (!limits || val === null || val === undefined) {
      return "blue.500"; // สีปกติ
    }
    if (val > limits.danger) {
      return "red.500"; // เกินเกณฑ์อันตราย
    }
    if (val > limits.warning) {
      return "orange.400"; // เกินเกณฑ์เฝ้าระวัง
    }
    return "blue.500"; // ปกติ
  };

  const valueColor = getValueColor(sensorKey, value);

  return (
    <VStack spacing={1} p={2} minW="80px">
      <Heading color="gray.500" size="sm">
        {label}
      </Heading>
      <VStack spacing={0} alignItems="center">
        <Heading color={valueColor} size="lg" lineHeight={1}>
          {displayValue(value)}
        </Heading>
        <Text color="gray.500" fontSize="xs">
          {unit}
        </Text>
      </VStack>
    </VStack>
  );
};


const DeviceCard = ({ mn, deviceLiveDetail }) => {
  const navigate = useNavigate();

  const cardBg = useColorModeValue("white", "gray.700");
  const headingColor = useColorModeValue("gray.700", "white");
  const dividerColor = useColorModeValue("green.500", "green.300");

  const { sensorData, isActive, timestamp } = deviceLiveDetail || {};

  const { signalColor, statusTooltip } = useMemo(() => {
    if (!deviceLiveDetail) {
      return { signalColor: "gray.400", statusTooltip: "Waiting for data..." };
    }
    return isActive
      ? { signalColor: "green", statusTooltip: `Active. Last seen: ${new Date(timestamp).toLocaleTimeString()}` }
      : { signalColor: "red", statusTooltip: `Inactive. Last seen: ${new Date(timestamp).toLocaleTimeString()} (more than 5 mins ago)` };
  }, [deviceLiveDetail, isActive, timestamp]);

  const sensorsToShow = [
    { key: 'pm25', label: 'PM2.5', unit: 'µg/m³' },
    { key: 'pm10', label: 'PM10', unit: 'µg/m³' },
    { key: 'tsp', label: 'TSP', unit: 'µg/m³' },
    { key: 'noise', label: 'Noise', unit: 'dB' },
  ];

  return (
    <Box
      borderWidth={1}
      borderColor={useColorModeValue("gray.200", "gray.600")}
      borderRadius="lg"
      p={4}
      bg={cardBg}
      shadow="md"
      transition="all 0.2s"
      _hover={{ shadow: "xl", transform: "translateY(-2px)" }}
    >
      <VStack align="stretch" spacing={3}>
        <Flex width="100%" alignItems="center">
          <Heading size="md" color={headingColor}>{mn}</Heading>
          <Tooltip label={statusTooltip} placement="top" hasArrow>
            <Box as="span" ml="auto">
              <FontAwesomeIcon icon={deviceLiveDetail ? faSignal : faCircleQuestion} size="lg" color={useColorModeValue(signalColor, signalColor)} />
            </Box>
          </Tooltip>
        </Flex>
        <Box w="100%" h="2px" bg={dividerColor} opacity={0.6} />
        <Wrap spacing={2} justify="space-around" p={2}>
          {sensorsToShow.map(sensor => {
            const value = sensorData?.[sensor.key];
            if (value !== null && value !== undefined) {
              return (
                <WrapItem key={sensor.key}>
                  {/* --- 3. อัปเดตการส่ง prop โดยเพิ่ม sensorKey เข้าไป --- */}
                  <SensorDisplay
                    sensorKey={sensor.key}
                    label={sensor.label}
                    value={value}
                    unit={sensor.unit}
                  />
                </WrapItem>
              );
            }
            return null;
          })}
        </Wrap>
        <Box w="100%" h="1px" bg={useColorModeValue("gray.200", "gray.600")} />
        <HStack justifyContent="center" spacing={4} pt={1}>
          <Button size="sm" colorScheme="blue" leftIcon={<FontAwesomeIcon icon={faClock} />} onClick={() => navigate(`/dashboard/${mn}`)}>Real-time</Button>
          <Button size="sm" colorScheme="orange" leftIcon={<FontAwesomeIcon icon={faClockRotateLeft} />} onClick={() => navigate(`/history?device=${mn}`)}>History</Button>
        </HStack>
      </VStack>
    </Box>
  );
};


// --- Component หลัก (AllDevicesDashboard) ไม่มีการแก้ไข ---
export default function AllDevicesDashboard() {
    // ... โค้ดส่วนนี้เหมือนเดิมทั้งหมด ...
    const navigate = useNavigate();
    const { devicesData } = useEnvData();
    const [configs, setConfigs] = useState({ devices: [], customers: [] });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
  
    const currentUser = useMemo(() => JSON.parse(localStorage.getItem("currentUser") || "{}"), []);
    const isSystemAdmin = currentUser.role === "system-admin";
  
    useEffect(() => {
      const fetchData = async () => {
        setIsLoading(true);
        setError(null);
        try {
          const token = localStorage.getItem("token");
          const headers = { headers: { Authorization: `Bearer ${token}` } };
          
          const deviceRes = await authFetch(`${process.env.REACT_APP_API_URL}/api/devices`, headers);
          const deviceData = await deviceRes.json();
          if (!deviceRes.ok) throw new Error(deviceData.error || 'Failed to fetch devices');
          
          let customerData = [];
          if (isSystemAdmin) {
            const custRes = await authFetch(`${process.env.REACT_APP_API_URL}/api/customers`, headers);
            customerData = await custRes.json();
            if (!custRes.ok) throw new Error(customerData.error || 'Failed to fetch customers');
          }
          
          setConfigs({
            devices: Array.isArray(deviceData) ? deviceData : [],
            customers: Array.isArray(customerData) ? customerData : [],
          });
        } catch (err) {
          setError(err.message);
        } finally {
          setIsLoading(false);
        }
      };
  
      fetchData();
    }, [isSystemAdmin]);
  
    const groupedByCustomer = useMemo(() => {
      if (isSystemAdmin) {
        return configs.customers.map(cust => ({
          customer: cust,
          devices: configs.devices.filter(d => d.customerId === cust.id)
        })).filter(group => group.devices.length > 0);
      }
      return [{
        customer: { name: "My Devices" },
        devices: configs.devices.filter(d => d.customerId === currentUser.customerId)
      }];
    }, [isSystemAdmin, configs, currentUser.customerId]);
  
  
    if (isLoading) {
      return <Center h="50vh"><Spinner size="xl" /></Center>;
    }
  
    if (error) {
      return <Center h="50vh"><Text color="red.500">Error: {error}</Text></Center>;
    }
  
    if (configs.devices.length === 0) {
      return <Center h="50vh"><Text>No devices configured.</Text></Center>;
    }
  
    return (
      <EnvDataProvider>
        <Box maxW="container.xl" mx="auto" p={5}>
          <Button
            onClick={() => navigate(-1)}
            leftIcon={<ChevronLeftIcon />}
            variant="ghost"
            width="80px"
          >
            Back
          </Button>
          <Heading mb={6} textAlign="center">All Devices Overview</Heading>
          <VStack spacing={10} align="stretch">
            {groupedByCustomer.map((group) => (
              <Box key={group.customer.id || group.customer.name}>
                <Heading size="lg" mb={4} color="gray.600" borderLeft="4px" borderColor="green.500" pl={3}>
                  {group.customer.name}
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={6}>
                  {group.devices.map((config) => (
                    <DeviceCard
                      key={config.id}
                      mn={config.id}
                      deviceLiveDetail={devicesData[config.id]}
                    />
                  ))}
                </SimpleGrid>
              </Box>
            ))}
          </VStack>
        </Box>
      </EnvDataProvider>
    );
}
